/**
 * Shared English locale — common strings reused across multiple modules.
 * Module-specific locales can spread these and override individual keys if needed.
 */

export const en = {
  // --- Buttons / Actions ---
  cancel: 'Cancel',
  confirm: 'Confirm',
  save: 'Save',
  delete: 'Delete',
  edit: 'Edit',
  close: 'Close',
  add: 'Add',
  remove: 'Remove',
  download: 'Download',
  upload: 'Upload',
  submit: 'Submit',
  back: 'Back',
  next: 'Next',
  yes: 'Yes',
  no: 'No',
  retry: 'Retry',
  search: 'Search',
  clear: 'Clear',
  apply: 'Apply',
  login: 'Login',
  continue: 'Continue',
  ok: 'OK',

  // --- States ---
  loading: 'Loading...',
  noResults: 'No results.',
  errorOccurred: 'Something went wrong',
  errorRetryMessage: 'Please try again later.',
  areYouSure: 'Are you sure?',

  // --- Common field labels / column headers ---
  id: 'ID',
  status: 'Status',
  details: 'Details',
  name: 'Name',
  createdOn: 'Created On',
  agent: 'Agent',
  customer: 'Customer',

  // --- Download feedback ---
  downloadStarted: 'Download started',
  downloadStartedDescription: 'Your document is being downloaded.',
  // --- Backend error messages (used by utils/helper errorHandler) ---
  // Keys are raw backend error identifiers/strings; values are user-facing messages.
  errorMessages: {
    required: 'Required',
    referralCode: 'Enter referral code',
    email: 'Invalid email address',
    phone: 'Invalid Phone number',
    alreadyRegistered: 'You have already been registered. Please sign in',
    groupName: 'Please add a group name',
    password: 'Invalid password',
    comparePassword: 'Both fields should have the same value',
    oldNewPassword: 'Old and new password cannot be same',
    wrongPassword: 'Wrong password',
    emailNotVerified: 'Email not verified',
    fileSize: 'File size cannot be bigger than 2MB',
    uploadFileRequired: 'Please Upload',
    'Missing required field': 'Required Field',
    savedSuccessfully: 'Information Saved Successfully',
    invalidPostalCode: 'Invalid Postal Code',
    unregisteredEmail: 'This email has not been registered.',
    confirmationText: 'Are you sure ?',
    invalidDateMessage: 'Invalid date format',
    minDateMessage: 'Date should not be before minimal date',
    'File is too large': 'ตัวไฟล์ใหญ่(เกิน 25 mb) จังไม่สามารถไปเปลี่ยนได้',
    invalidFormat: 'Invalid Format',
    vehicleWeight: 'Please input weight within range',
    noInsurerInFairdee: 'FairDee has not yet proceeded with this insurance company.',
    incorrectInfo: 'Incorrect Information',
    successfulSignup: 'Signed in Successfully',
    updatedSuccessfully: 'Updated Successfully',
    copied: 'Copied',
    loseData: 'Are you sure you want to move away? Data might be lost.',
    noBrokerMessage:
      'If you do not have a broker license, you will have a membership status which entitles you to buy insurance with a discount, but you will not be able to generate quotation and receive commission',
    confirmationQuestion: 'Are you sure?',
    maxDiscount: 'ส่ว่นลดสูงสุดคือ',
    baht: 'บาท',
    lessValue: 'Value should be less than',
    characters: 'characters',
    moreValue: 'Value should be more than',
    upload: 'Upload',
    linkCopied: 'Link Copied',
    successfullFileUpload: 'File uploaded Successfully',
    accepted: 'Accepted',
    rejected: 'Rejected',
    takeAction: 'Take Action',
    select: 'Select',
    deletedSuccessfully: 'Deleted Successfully',
    createdSuccessfully: 'Created Succesfully',
    'Ensure this field has no more than 10 characters.': 'Ensure this field has no more than 10 characters.',
    'This value does not match the required pattern.': 'This value does not match the required pattern.',
    'Invalid value specified': 'Invalid value specified',
    broker_license_number: 'Broker license number',
    reconciled: 'Reconciled',
    contractExists: 'Contract Already Exists. Are you sure to regenerate?',
    generatedSuccessfully: 'Generated Successfully',
    will_pay_in_instalments_limit: 'Making this purchase will exceed the limit of 75000 Baht',
    moreThanOrEqual: 'Value should be more than or equal to',
    resetDiscount: 'Are you sure want to continue? Discount will be reset to 0.',
    resetPasswordEmailSent: 'Reset password email has been sent.',
    'Invalid credentials given.': 'Invalid credentials given.',
    bannedUser: 'Please contact fairdee support to activate your account',
    'Running cover/purchase request already exists for this national ID':
      'Running cover/purchase request already exists for this national ID',
    emailAlreadyExists: 'Duplicate Email. Please use a different email',
    uploadPolicy: 'Please upload policy',
    ac_number: 'Account Number',
    'Not eligible for free vaccine insurance': 'Not eligible for free vaccine insurance',
    'Free insurance already availed, check Policy in progress table':
      'Free insurance already availed, check Policy in progress table',
    'Policy Number already exists for another policy.': 'Policy Number already exists for another policy.',
    purchasedSuccessfully: 'Policy Purchased Successfully',
    'Failed to upload policy into the system': 'Failed to upload policy into the system',
    incorrectCalculationGrossPremium: '(Gross Premium + Tax + Duty) - Premium After Tax must be less than equal to 1',
    'Branch Code can only have 4 characters': 'Branch Code must contain 4 digits',
    'Cannot downgrade level': 'Cannot downgrade level',
    'Cannot update once endorsement request is sent to insurer. For any updates you must delete this and create new endorsement request.':
      'Cannot update once endorsement request is sent to insurer. For any updates you must delete this and create new endorsement request.',
    'Cannot update once endorsement is approved.': 'Cannot update once endorsement is approved.',
    'Endorsement for cancellation of cmi is already recorded in system':
      'Endorsement for cancellation of cmi is already recorded in system',
    'Please reconcile the payment in customer invoice': 'Please reconcile the payment in customer invoice',
    'Will pay in instalments not allowed for inspection_garage':
      'Will pay in instalments not allowed for Inspection Garage',
    'Will pay in instalments not allowed for subagent': 'Will pay in instalments not allowed for subagent',
    'Please enter a customer number different from agent phone number':
      'Please enter a customer number different from agent phone number',
    'Your order is processing.': 'Your order is processing.',
    'Please do not reload or close this page': 'Please do not reload or close this page',
    failedGetDescData: 'Failed to get description data. Please contact our support',
    'Image failed to meet OCR requirements, please try again':
      'Image failed to meet OCR requirements, please try again',
    'Use of I, O, Q is restricted': 'Use of I, O, Q is restricted',
    'Engine number can’t exceed 17 characters': 'Engine number can’t exceed 17 characters',
    'Up to 17 characters only': 'สูงสุด 17 ตัวอักษรเท่านั้น',
    'ห้ามคีย์ช่องว่างและภาษาไทยและอักขระ พิเศษลงในช่องเลขตัวถัง':
      'ห้ามคีย์ช่องว่างและภาษาไทยและอักขระ พิเศษลงในช่องเลขตัวถัง',
    'Use of ฏ, ฃ, ฅ, ซ is restricted': 'Use of ฏ, ฃ, ฅ, ซ is restricted',
    '3-8 characters only': '3-8 characters only',
    'Don’t enter province': 'Don’t enter province',
    'The vehicle number is wrong': 'The vehicle number is wrong',
    'กรุณาใส่ - หรือเว้นว่างเพื่อขีดขั้นเลขทะเบียน เช่น 2รน-2343 หรือ 2รน 2343':
      'Please enter - or leave blank to limit the registration number, such as 2RON-2343 or 2RON 2343',
    'กรุณาใส่ - เพื่อขีดขั้น ในช่องทะเบียนรถ': 'กรุณาใส่ - เพื่อขีดขั้น ในช่องทะเบียนรถ',
    'เลขทะเบียนรถผิด(อักษรไทย-อังกฤษและตัวเลขเท่านั้น)': 'เลขทะเบียนรถผิด(อักษรไทย-อังกฤษและตัวเลขเท่านั้น)',
    หมายเลขทะเบียนรถควรประกอบด้วยตัวอักษรภาษาไทยและตัวเลขเท่านั้น:
      'หมายเลขทะเบียนรถควรประกอบด้วยตัวอักษรภาษาไทยและตัวเลขเท่านั้น',
    'Added By AI': 'Added By AI',
    'Something Went Wrong!': 'Something Went Wrong!',
  },

  // --- Global header filters ---
  owner: 'Owner',
  ownerSelectedCount: '{{count}} selected',
  ownerSearchPlaceholder: 'Search staff...',
  ownerNoResults: 'No staff found.',
  ownerClearAll: 'Clear all owners',

  // --- Date range filter ---
  dateRangeTo: 'to',
  dateRangeEmpty: '00 Mth to 00 Mth',
  dateRangeMaxExceeded: 'Date range cannot exceed {{months}} months. Please pick a shorter range.',

  // --- Document preview (shared block) ---
  documentPreviewLoadingPdf: 'Loading PDF…',
  documentPreviewFailedPdf: 'Failed to load PDF document',
  documentPreviewFailedImage: 'Failed to load image',
  documentPreviewFailedDocument: 'Failed to load document',
  documentPreviewUnsupported: 'Unsupported file type',
  documentPreviewPrevious: 'Previous',
  documentPreviewNext: 'Next',
  documentPreviewPageOf: '{{page}} of {{total}}',
  documentPreviewShare: 'Share',
  documentPreviewDownload: 'Download',
  documentPreviewDownloadFile: 'Download file',
  documentPreviewUrlCopied: 'URL copied to clipboard',
  documentPreviewPasswordRequired: 'Password is required to view this document. You can download it instead.',
  documentPreviewPasswordPrompt: 'This PDF is password-protected. Enter password:',
  documentPreviewPasswordIncorrect: 'Incorrect password. Try again:',
  documentPreviewZoomIn: 'Zoom in',
  documentPreviewZoomOut: 'Zoom out',
  documentPreviewResetView: 'Reset view',
  documentPreviewNoDocument: 'No document',
  documentPreviewFallbackName: 'Document',
  // --- Cancellation reasons (shared: policy-in-progress form, endorsement detail, ops card) ---
  endorsementCancellationReasonIncompleteInfo: 'Incomplete information — unable to contact customer to verify details',
  endorsementCancellationReasonVehicleInspection:
    'Unable to schedule vehicle inspection or confirm damage/injury with customer',
  endorsementCancellationReasonInsurerRejected: 'Insurer rejected the sale reporting ',
  endorsementCancellationReasonPaymentDefault: 'Customer payment default / Overdue payment',
  endorsementCancellationReasonCustomerRequest: 'Customer requests policy cancellation',

  // --- Endorsement request scope ---
  requestTypeExternal: 'External Request',
  requestTypeExternalDesc: 'Initiated by customer or agent on behalf of customer.',
  requestTypeInternal: 'Internal Request',
  requestTypeInternalDesc: 'Initiated internally by Fairdee staff.',

  // --- Licence-plate format messages (shared/blocks/plate; information-correction + ops Step 2) ---
  plateRestrictedCharacters: 'Use of ฏ, ฃ, ฅ, ซ is restricted',
  plateLength: '3-8 characters only',
  plateLengthAzay: '1-11 characters only',
  plateNoProvince: 'Don’t enter the province',
  plateInvalid: 'The vehicle number is wrong',
  plateSeparatorRequired: 'Please add a space or a hyphen (-).',
  plateHyphenRequired: 'Add a hyphen (-) to separate the vehicle number',
  plateSeparatorExample: 'Separate the vehicle number with a hyphen or a space, e.g. 2รน-2343 or 2รน 2343',
  plateNoHyphen: 'Invalid vehicle number (Thai/English letters and digits only)',
  plateThaiDigitsOnly: 'The vehicle number must contain Thai letters and digits only',
  cancellationBlocksReporting: 'This policy is currently under cancellation, so it cannot be reported.',

  // --- Agent credit type badge (FTT-16588), see shared/creditType.ts ---
  creditTypePrepaid: 'Prepaid',
  creditTypePostpaid: 'Postpaid',
} as const;
